import type { ComboChartAggregation, ViewConfigDimension } from '@salescore/core'
import { isNotNullish } from 'effect/Predicate'
import { useRecoilState } from 'recoil'

import { kpiParameterAtom } from '../recoil/navigation/atoms'

interface UseComboChartAggregationResult {
  aggregation: ComboChartAggregation | undefined
  setXAxis: (dimension: ViewConfigDimension | undefined) => void
  setBreakdown: (dimension: ViewConfigDimension | undefined) => void
}

export function useComboChartAggregation(): UseComboChartAggregationResult {
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const { rows } = kpiPivotParameter.pivot
  const aggregation = rowsToComboAggregation(rows)

  const setXAxis = (dimension: ViewConfigDimension | undefined): void => {
    setKpiPivotParameter((oldValue) => ({
      ...oldValue,
      pivot: {
        rows: [dimension, ...oldValue.pivot.rows.slice(1)].filter(isNotNullish),
        columns: [],
      },
    }))
  }

  const setBreakdown = (dimensionCategory: ViewConfigDimension | undefined): void => {
    setKpiPivotParameter((oldValue) => ({
      ...oldValue,
      pivot: {
        rows: [oldValue.pivot.rows[0], dimensionCategory].filter(isNotNullish),
        columns: [],
      },
    }))
  }

  return { aggregation, setXAxis, setBreakdown }
}

export function rowsToComboAggregation(rows: readonly ViewConfigDimension[]): ComboChartAggregation | undefined {
  const xAxis = rows[0]
  if (xAxis === undefined) return undefined
  return { chartFormat: 'combo', xAxis, breakdown: rows[1] }
}

export function comboAggregationToRows(aggregation: ComboChartAggregation): ViewConfigDimension[] {
  const rows: ViewConfigDimension[] = [aggregation.xAxis]
  if (aggregation.breakdown !== undefined) rows.push(aggregation.breakdown)
  return rows
}
